import React from 'react';
import { Alert } from '@mui/material';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';

/**
 * Why the patient in the URL is not in this investigation's loaded list (SHER-258).
 *
 * - `deleted`: soft-deleted in this investigation; `deletedAt` is when.
 * - `not-found`: anything else the API will not tell apart — another investigation, a uuid
 *   that does not exist, a mistyped link. Deliberately one message.
 * - `not-loaded`: active in this investigation, but the local list has not caught up.
 */
export type PatientUnavailableReason = 'deleted' | 'not-found' | 'not-loaded';

interface Props extends LocalizeContextProps {
    reason: PatientUnavailableReason;
    deletedAt?: string;
}

const SEVERITY: Record<PatientUnavailableReason, 'info' | 'warning'> = {
    'deleted': 'info',
    'not-found': 'warning',
    'not-loaded': 'info',
};

const PatientUnavailableNotice: React.FC<Props> = ({ reason, deletedAt, activeLanguage }) => {
    const formatDate = (isoDate: string) => {
        const date = new Date(isoDate);
        if (Number.isNaN(date.getTime())) {
            return isoDate;
        }
        return date.toLocaleString(activeLanguage ? activeLanguage.code : undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
        });
    };

    return (
        <Alert severity={SEVERITY[reason]} data-testid="patient-unavailable">
            {reason === 'deleted' && <Translate id="hospital.patient.error.deleted" data={{ date: deletedAt ? formatDate(deletedAt) : '' }} />}
            {reason === 'not-found' && <Translate id="hospital.patient.error.no-patient" />}
            {reason === 'not-loaded' && <Translate id="hospital.patient.error.not-loaded" />}
        </Alert>
    );
};

export default withLocalize(PatientUnavailableNotice);
